/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-09-26 11:39:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import { Layout, SkeletonBodyText, SkeletonPage, Card } from '@shopify/polaris';

import General from './general';
import VisualEditor from './visual-editor';
import Placement from './placement';
import PlacementSSR from './placement-ssr';

import {
  enterSABFullscreen,
  exitSABFullscreen
} from 'src/integrations/shopify-app-bridge/fullscreen';
import { routes } from 'src/routes';
import { hasPermissionRecommendation } from 'src/helpers/permission-helper';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';

import { RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES as RECOMMENDATION_TYPES } from 'src/constants/recommendation';

import * as recommendationAction from 'src/stores/actions/recommendation-actions';
import { handleOpenFullscreen } from 'actions/app-integration-actions';
import * as FilterDataActions from 'actions/filter-data-actions';
import * as themeSetupV3Action from 'actions/theme-setup-v3-actions';

import type { ComposedProps, State } from './index.type';
import * as ThemeSettingAction from 'src/stores/actions/theme-settings-actions';
import * as themeIntegrationAction from 'actions/shopify-integration/theme-integration-actions';

import history from 'src/routes/history';
// import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';

class WidgetItem extends Component<ComposedProps, State> {
  unsubscribeExit = undefined;

  state = {
    currentStep: 0,
    dirtyOnStep: { step_0: false, step_1: false },
    widget: {},
    activeModalConfirm: false,
    selectedTheme: {},
    aimlPermission: false,
    versionOS: 0
  };

  componentDidMount() {
    const { getRecommendationWidget, accessScopes, getAllThemes, allThemes, checkEnableBoostCore } =
      this.props;
    this.handleOpenFullscreenPage();

    if (_.isEmpty(allThemes)) {
      getAllThemes();
    } else {
      this.processTheme(allThemes);
    }

    const { widgetId } = this.props.match.params;
    if (!!widgetId) {
      getRecommendationWidget(widgetId);
    }

    if (hasPermissionRecommendation(accessScopes)) {
      this.setState({ aimlPermission: true });
    }

    // Get enable boost core on live theme
    checkEnableBoostCore({});
  }

  componentWillReceiveProps(nextProps) {
    const {
      loading,
      getFilterData,
      allThemes: nextAllThemes,
      getAllThemesLoading: nextGetAllThemesLoading,
      hasAddonAIPowerRecommendation,
      recommendationWidget
    } = nextProps;
    const { loading: prevLoading, getAllThemesLoading } = this.props;

    // Loaded widget item
    if (!loading && prevLoading) {
      // Update state current step
      this.setState({ widget: recommendationWidget });

      const recommendationType = _.get(recommendationWidget, 'recommendationType', '');

      if (
        recommendationType === RECOMMENDATION_TYPES['related-items'].value ||
        recommendationType === RECOMMENDATION_TYPES['frequently-bought-together'].value
      ) {
        if (!hasAddonAIPowerRecommendation) history.push(routes.recommendationWidgets.list());

        getFilterData();
      }

      if (!_.isEmpty(recommendationType) && !RECOMMENDATION_TYPES[recommendationType].hasSettings) {
        this.setState({ currentStep: 1 });
      }
    }

    if (getAllThemesLoading && !nextGetAllThemesLoading) {
      this.processTheme(nextAllThemes);
    }
  }

  componentWillUnmount() {
    this.unsubscribeExit();
  }

  processTheme = allThemes => {
    const mainTheme = _.find(allThemes, theme => theme.role === 'main') || {};
    const { isOS20 } = mainTheme;
    let versionOS = isOS20 ? 2 : 1;

    this.setState({ selectedTheme: mainTheme, versionOS });
    this.getThemeSetting(mainTheme);
  };

  async getThemeSetting(selectedTheme) {
    const { getThemeSettingById, checkThemeIntegrationStatus, isEnableShopifyIntegration } =
      this.props;
    const themeId = selectedTheme.id;
    if (themeId) {
      getThemeSettingById(themeId);
      if (isEnableShopifyIntegration) {
        checkThemeIntegrationStatus(themeId);
      }
    }
  }

  render() {
    const { currentStep, dirtyOnStep, widget, selectedTheme, versionOS } = this.state;
    const { isEnableShopifyIntegration } = this.props;
    const { recommendationType } = widget;

    if (!recommendationType) {
      return (
        <SkeletonPage primaryAction fullWidth={false}>
          <Layout>
            <Layout.Section>
              <Card sectioned>
                <SkeletonBodyText lines={5} />
              </Card>
            </Layout.Section>
            <Layout.Section>
              <Card sectioned>
                <SkeletonBodyText lines={5} />
              </Card>
            </Layout.Section>
          </Layout>
        </SkeletonPage>
      );
    }

    let content = '';

    switch (currentStep) {
      case 0: {
        // render General
        content = (
          <General
            isCreateFlow={false}
            widget={widget}
            handleCloseFullscreenPage={this.handleCloseFullscreenPage}
            currentStep={currentStep}
            dirtyOnStep={dirtyOnStep}
            updateState={this.updateState}
          />
        );
        break;
      }
      case 1: {
        // render design
        content = (
          <VisualEditor
            isCreateFlow={false}
            widget={widget}
            handleCloseFullscreenPage={this.handleCloseFullscreenPage}
            currentStep={currentStep}
            dirtyOnStep={dirtyOnStep}
            updateState={this.updateState}
            selectedTheme={selectedTheme}
          />
        );
        break;
      }
      case 2: {
        // render placement
        content = isEnableShopifyIntegration ? (
          <PlacementSSR
            isCreateFlow={false}
            handleCloseFullscreenPage={this.handleCloseFullscreenPage}
            versionOS={versionOS}
          />
        ) : (
          <Placement
            isCreateFlow={false}
            handleCloseFullscreenPage={this.handleCloseFullscreenPage}
            versionOS={versionOS}
          />
        );
        break;
      }
      default:
    }

    return (
      <div className="BC-Page BC-Page__RecommendationWidgetItem">
        <div className="setup-content">{content}</div>
      </div>
    );
  }

  // Handle action opening the visual editor
  handleOpenFullscreenPage = () => {
    const { handleOpenFullscreen } = this.props;
    this.unsubscribeExit = enterSABFullscreen(() => {
      this.backToManagementPage();
      handleOpenFullscreen(false);
    });

    handleOpenFullscreen(true);
  };

  // Handle action closing the fullscreen
  handleCloseFullscreenPage = (runExitFullscreen = true) => {
    const { handleOpenFullscreen } = this.props;
    this.backToManagementPage();
    if (runExitFullscreen) {
      exitSABFullscreen();
      handleOpenFullscreen(false);
    }
  };

  // Handle back to management page
  backToManagementPage = () => {
    const { history } = this.props;
    history.push(routes.recommendationWidgets.list());
  };

  // Handle update state
  updateState = (fieldName, value) => {
    this.setState({ [fieldName]: value });
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Get recommendation widgets
  getRecommendationWidget: (widgetId: string) =>
    dispatch(recommendationAction.getRecommendationWidget(widgetId, ownProps.i18n)),

  handleOpenFullscreen: (isOpenFullScreen: boolean) =>
    dispatch(handleOpenFullscreen(isOpenFullScreen)),

  getFilterData: () => dispatch(FilterDataActions.getFilterData(ownProps.i18n, true)),
  getAllThemes: () => dispatch(themeSetupV3Action.getAllThemes(ownProps.i18n)),
  getThemeSettingById: id =>
    dispatch(ThemeSettingAction.getThemeSettingById(id, ownProps.i18n, 'recommendation')),
  checkThemeIntegrationStatus: themeId =>
    dispatch(themeIntegrationAction.checkThemeIntegrationStatus(ownProps.i18n, themeId)),
  // Check enable boost core on live theme
  checkEnableBoostCore: queries =>
    dispatch(recommendationAction.checkEnableBoostCore(queries, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const recommendationWidget = _.get(state.recommendation, 'recommendationWidget', {});
  const accessScopes = _.get(state.oauth, 'tenantApp.accessScopes', {});
  const hasAddonAIPowerRecommendation = _.get(
    state.oauth.tenantApp,
    'settings.recommendation.settings.frequently-bought-together',
    false
  );

  return {
    loading: _.get(state.recommendation, 'loading', false),
    success: _.get(state.recommendation, 'success', false),
    host: _.get(state.oauth, 'host', ''),
    accessScopes,
    allThemes: _.get(state.themeSetupV3, 'allThemes', []),
    getAllThemesLoading: _.get(state.themeSetupV3, 'getAllThemesLoading', false),
    hasAddonAIPowerRecommendation,
    recommendationWidget,
    isEnableShopifyIntegration: enableShopifyIntegration(state.oauth.tenantApp)
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(WidgetItem));
